import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { 
  Plus, 
  Video, 
  Code, 
  Eye, 
  Edit3, 
  Trash2, 
  Send,
  Users,
  MessageCircle,
  Heart,
  Share2,
  Calendar
} from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

const AdminNetworkFeed = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  
  // New post state
  const [newPost, setNewPost] = useState({
    title: '',
    description: '',
    content_type: 'video', // video, text, announcement
    html_content: '',
    video_embed: '',
    is_featured: false,
    allow_comments: true
  });

  // HTML Editor state
  const [htmlPreview, setHtmlPreview] = useState(false);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.ADMIN}/network/posts`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
      toast.error('Failed to load network posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPost = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_ENDPOINTS.ADMIN}/network/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newPost)
      });

      if (response.ok) {
        toast.success('Network post published successfully!');
        setNewPost({
          title: '',
          description: '',
          content_type: 'video',
          html_content: '',
          video_embed: '',
          is_featured: false,
          allow_comments: true
        });
        setShowEditor(false);
        loadPosts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to publish post');
      }
    } catch (error) {
      console.error('Error publishing post:', error);
      toast.error('Failed to publish post');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.ADMIN}/network/posts/${postId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Post deleted successfully');
        loadPosts();
      } else {
        toast.error('Failed to delete post');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Network Feed</h1>
          <p className="text-gray-600 mt-1">
            Manage community content and announcements
          </p>
        </div>
        
        <Button 
          onClick={() => setShowEditor(!showEditor)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          {showEditor ? 'Cancel' : 'New Post'}
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{posts.length}</p>
                <p className="text-gray-600 text-sm">Total Posts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">145</p>
                <p className="text-gray-600 text-sm">Active Members</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Heart className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">2.4k</p>
                <p className="text-gray-600 text-sm">Total Engagement</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Video className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{posts.filter(p => p.content_type === 'video').length}</p>
                <p className="text-gray-600 text-sm">Video Posts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Post Editor */}
      {showEditor && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Code className="h-5 w-5 mr-2" />
              Create Network Post
            </CardTitle>
            <CardDescription>
              Share updates, videos, and announcements with all members
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmitPost} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Post Title *</Label>
                  <Input
                    id="title"
                    value={newPost.title}
                    onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                    placeholder="Enter post title..."
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content_type">Content Type</Label>
                  <select
                    id="content_type"
                    value={newPost.content_type}
                    onChange={(e) => setNewPost({...newPost, content_type: e.target.value})}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  >
                    <option value="video">Video Content</option>
                    <option value="text">Text Post</option>
                    <option value="announcement">Announcement</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newPost.description}
                  onChange={(e) => setNewPost({...newPost, description: e.target.value})}
                  placeholder="Brief description of the post..."
                  rows={3}
                />
              </div>

              {/* HTML/Video Editor Tabs */}
              <Tabs defaultValue="html" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="html">
                    <Code className="h-4 w-4 mr-2" />
                    HTML Editor
                  </TabsTrigger>
                  <TabsTrigger value="video">
                    <Video className="h-4 w-4 mr-2" />
                    Video Embed
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="html" className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label>HTML Content</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setHtmlPreview(!htmlPreview)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      {htmlPreview ? 'Edit' : 'Preview'}
                    </Button>
                  </div>

                  {htmlPreview ? (
                    <div className="border rounded-lg p-4 min-h-[200px] bg-white">
                      <div dangerouslySetInnerHTML={{ __html: newPost.html_content }} />
                    </div>
                  ) : (
                    <Textarea
                      value={newPost.html_content}
                      onChange={(e) => setNewPost({...newPost, html_content: e.target.value})}
                      placeholder="Paste your HTML code here... (YouTube embeds, custom video players, etc.)"
                      rows={10}
                      className="font-mono text-sm"
                    />
                  )}

                  <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                    <strong>💡 HTML Editor Tips:</strong>
                    <ul className="mt-2 space-y-1">
                      <li>• Paste YouTube embed codes: &lt;iframe src="https://youtube.com/embed/VIDEO_ID"&gt;</li>
                      <li>• Add Vimeo players: &lt;iframe src="https://player.vimeo.com/video/VIDEO_ID"&gt;</li>
                      <li>• Custom video players and streaming services supported</li>
                      <li>• Use Bootstrap classes for responsive design</li>
                    </ul>
                  </div>
                </TabsContent>

                <TabsContent value="video" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="video_embed">Video Embed Code</Label>
                    <Textarea
                      id="video_embed"
                      value={newPost.video_embed}
                      onChange={(e) => setNewPost({...newPost, video_embed: e.target.value})}
                      placeholder="Paste video embed code here (YouTube, Vimeo, etc.)"
                      rows={6}
                    />
                  </div>

                  {newPost.video_embed && (
                    <div className="space-y-2">
                      <Label>Video Preview</Label>
                      <div className="border rounded-lg p-4 bg-white">
                        <div dangerouslySetInnerHTML={{ __html: newPost.video_embed }} />
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {/* Post Options */}
              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={newPost.is_featured}
                    onChange={(e) => setNewPost({...newPost, is_featured: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Featured Post</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={newPost.allow_comments}
                    onChange={(e) => setNewPost({...newPost, allow_comments: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Allow Comments</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowEditor(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="btn-primary"
                  disabled={loading}
                >
                  <Send className="h-4 w-4 mr-2" />
                  {loading ? 'Publishing...' : 'Publish Post'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Posts List */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">Recent Posts</h2>
        
        {loading && posts.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No posts yet</h3>
              <p className="text-gray-600 mb-4">
                Create your first network post to engage with members
              </p>
              <Button onClick={() => setShowEditor(true)} className="btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                Create First Post
              </Button>
            </CardContent>
          </Card>
        ) : (
          posts.map((post) => (
            <Card key={post.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CardTitle className="text-lg">{post.title}</CardTitle>
                      {post.is_featured && (
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                          Featured
                        </Badge>
                      )}
                      <Badge variant="outline" className="capitalize">
                        {post.content_type}
                      </Badge>
                    </div>
                    <CardDescription>{post.description}</CardDescription>
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(post.created_at)}
                      <span className="mx-2">•</span>
                      <Users className="h-4 w-4 mr-1" />
                      {post.views || 0} views
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm">
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDeletePost(post.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              {(post.html_content || post.video_embed) && (
                <CardContent>
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <div dangerouslySetInnerHTML={{ 
                      __html: post.html_content || post.video_embed 
                    }} />
                  </div>
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminNetworkFeed;